import {
  forwardRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ElementType,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { T } from '@territory/tokens';
import { marketingDialogCloseCss } from '../internal/marketingDialogButton.js';
import { marketingScrollCss } from '../internal/marketingScroll.js';
import { marketingMenuCss } from '../internal/marketingNav.js';
import { marketingThemeCycleCss } from '../internal/marketingTheme.js';
import { DENSE_PHONE, duration, radius, TOUCH_MIN } from '../scale.js';
import { useStyleOnce } from '../internal/useStyleOnce.js';
import { usePrefersReducedMotion } from '../internal/usePrefersReducedMotion.js';
import { focusRing } from '../internal/focusRing.js';
import { CountBadge, countLabel } from '../internal/CountBadge.js';
import { renderIconSlot, type IconSlot } from '../internal/iconSlot.js';
import { DRAWER_BTN_H, DRAWER_BTN_R, DRAWER_IB_ICON, drawerButtonCss } from '../internal/drawerButton.js';
import { consoleRole } from '../internal/consoleRoles.js';
import {
  WEDGE_ICON_BUTTON_CSS,
  WEDGE_ICON_BUTTON_DEFAULT,
  WEDGE_ICON_BUTTON_GLYPH,
  WEDGE_ICON_BUTTON_MIN,
} from '../internal/wedgeIconButton.js';
import { RECON_HOVER_MS, RECON_PRESS_MS, reconRole } from '../internal/reconRoles.js';
import { SHOP_FLOAT_CSS } from '../internal/shopFloat.js';

/* 'ctrl': the console dashboard Customize widget control (legacy .wctrls button: move, resize, remove), a 28px square
   with an 8px radius and a hairline edge on the elevated fill. Grows to 38px (legacy) at 860px and below with size="dense".
   'drawer': the owner console Users drawer icon button (legacy .dv-btn.dv-ib: close, previous, next), a 34px square
   from the --console-dv-* roles. `size` is ignored; it grows to TOUCH_MIN at 860px and below. 'recon': the shipped
   Recon .icon-btn, a 44px glass square. 'recon-overlay': the same over a photo (.lightbox .icon-btn), a dark glass fill
   in both themes. 'wedge-bv2': the Wedge Brief V2 icon button (Bv2IconButton), a 12px-radius square with a hairline: box
   max(discSize, 40) floored at 44, 18px glyph, hover and `pressed` fill accent-soft, 1px lift on hover; `size` is ignored,
   `color`, `badge` and `dot` are not drawn. 'recon-step': the shipped .ps-stepper-ctl button, a 40px borderless key with a 20px glyph. The recon
 looks draw the icon bare, with no disc. 'marketing-theme': the Marketing theme cycle, one box from discSize
   (36px by default), no disc or press transform. 'shop-float': the storefront back-to-top button (legacy shop .to-top), a 46px
   blurred circle with a hairline that lights to accent on hover; the glyph (an img or svg) turns 90deg on hover. The
   sheet paints the circle only; fixed position and show or hide stay in the app. `size` is ignored.
   'marketing-scroll': the public site's native BackToTop button, a 44px glass circle with six 240ms transitions.
   Its inline radius retains priority over the page focus rule. Fixed layout and visibility stay in the app. */
export type IconButtonVariant = 'plain' | 'filled' | 'solid' | 'drawer' | 'ctrl' | 'recon' | 'recon-overlay' | 'recon-step' | 'wedge-bv2' | 'marketing-theme' | 'marketing-dialog-close' | 'marketing-menu' | 'shop-float' | 'marketing-scroll';
/* 'dense': consoles only, desktop only: a 28px box with no 44px wrapper (legacy owner .btn.sm height). Grows to
   TOUCH_MIN at 860px and below. */
export type IconButtonSize = 'dense' | 'sm' | 'md' | 'lg';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'style' | 'children' | 'color' | 'aria-label'> {
  icon: IconSlot;
  /* Required: there is no visible text to name the control. */
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /* A T.* value for the glyph. */
  color?: string;
  /* Visual count; folded into the accessible name ("Inbox, 3 new"). */
  badge?: number;
  /* Activity dot; folded into the accessible name. */
  dot?: boolean;
  /* Toggle state, sets aria-pressed. */
  pressed?: boolean;
  /* Disc diameter override, for the wedge TabBar palette coin. */
  discSize?: number;
  href?: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

/* verbatim legacy .wctrls button, off-scale */
const CTRL_BOX = 28;
const CTRL_R = 8;
/* Legacy phone size of .wctrls button; WCAG 2.5.8 asks 24px. */
const CTRL_PHONE = 38;
/* verbatim wedge, off-scale */
const DISC_SM = 30;
const DISC_MD = 36;
const DOT = 8;
const DOT_INSET = 2;
const DOT_RIM = 1.5;
const HAIRLINE = 0.5;
const PRESS_SCALE = 0.97;
const DISABLED_OPACITY = 0.4;

const DENSE = 28;

const SIZES: Record<IconButtonSize, { disc: number; glyph: number }> = {
  dense: { disc: DENSE, glyph: 14 },
  sm: { disc: DISC_SM, glyph: 16 },
  md: { disc: DISC_MD, glyph: 20 },
  lg: { disc: TOUCH_MIN, glyph: 22 },
};

/* legacy .dv-ib draws its glyph at stroke 2 */
const DRAWER_GLYPH_WEIGHT = 2;
/* Recon looks: the shipped Recon rules, one for one, in their shipped order and at their shipped specificity (:where()
   pads each selector to the class selector it replaces). The sheet goes in first (useStyleOnce `first`), so Recon's
   own layout classes on the same element still win ties. Recon's .stepper sizes and .spin animation stay Recon
   compositions. */
const RECON = ':where([data-rv-icon-button]):is([data-rv-variant="recon"], [data-rv-variant="recon-overlay"])';
/* shipped .ps-stepper-ctl button, off-scale */
const STEP_BOX = 40;
const STEP_GLYPH = 20;
const STEP = ':where([data-rv-icon-button])[data-rv-variant="recon-step"]';
const RECON_CSS = `${RECON} { display: inline-grid; place-items: center; width: ${TOUCH_MIN}px; height: ${TOUCH_MIN}px; flex: none; border-radius: ${radius.lg}px; border: 1px solid ${T.separator}; background: ${T.surfaceAlt}; color: ${T.label}; transition: ${['transform', 'border-color', 'background'].map((p) => `${p} ${RECON_HOVER_MS}ms ${T.ease}`).join(', ')}; }
${RECON}:active { transform: scale(0.96); }
@media (hover: hover) { ${RECON}:hover { border-color: ${T.separatorBold}; background: ${reconRole('button-hover', T.surfaceAlt)}; } }
${RECON}[disabled] { opacity: 0.5; }
[data-rv-icon-button][data-rv-variant="recon-overlay"] { background: ${reconRole('overlay-fill', T.bgElev)}; border-color: ${reconRole('overlay-edge', T.separatorBold)}; color: ${reconRole('overlay-ink', T.label)}; }
${RECON}:active { transition-duration: ${RECON_PRESS_MS}ms; }
${STEP} { width: ${STEP_BOX}px; height: ${STEP_BOX}px; border-radius: ${radius.sm}px; border: 0; background: transparent; font-size: ${STEP_GLYPH}px; line-height: 1; color: ${T.label}; }
@media (hover: hover) { ${STEP}:not([disabled]):hover { background: ${reconRole('button-hover', T.surfaceAlt)}; } }
${STEP}[disabled] { opacity: 0.35; cursor: default; }`;

const CSS = `${focusRing('[data-rv-icon-button]:not(:where([data-rv-variant="marketing-menu"], [data-rv-variant="marketing-scroll"]))')}
${marketingScrollCss('[data-rv-icon-button][data-rv-variant="marketing-scroll"]')}
${marketingMenuCss('[data-rv-icon-button][data-rv-variant="marketing-menu"]')}
${marketingThemeCycleCss('[data-rv-icon-button][data-rv-variant="marketing-theme"]')}
${marketingDialogCloseCss('[data-rv-icon-button][data-rv-variant="marketing-dialog-close"]')}
${DENSE_PHONE} { [data-rv-icon-button][data-rv-size="dense"] { min-width: ${TOUCH_MIN}px; min-height: ${TOUCH_MIN}px; } }
${DENSE_PHONE} { [data-rv-icon-button][data-rv-size="dense"][data-rv-variant="ctrl"] { min-width: ${CTRL_PHONE}px; min-height: ${CTRL_PHONE}px; } }
${drawerButtonCss('[data-rv-icon-button][data-rv-variant="drawer"]')}`;

export const IconButton = forwardRef<HTMLElement, IconButtonProps>(function IconButton(
  {
    icon,
    'aria-label': label,
    variant = 'plain',
    size = 'md',
    color,
    badge,
    dot = false,
    pressed,
    discSize,
    href,
    disabled = false,
    className,
    style,
    type,
    onClick,
    onPointerDown,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    ...rest
  },
  ref,
) {
  useStyleOnce('icon-button', CSS);
  /* Called before the recon sheet: each first-sheet insert lands ahead of the last, so recon stays the first style in head. */
  useStyleOnce('icon-button-shop-float', SHOP_FLOAT_CSS, { first: true });
  useStyleOnce('icon-button-recon', RECON_CSS, { first: true });
  useStyleOnce('icon-button-wedge-bv2', WEDGE_ICON_BUTTON_CSS);
  const reduced = usePrefersReducedMotion();
  const [press, setPress] = useState(false);
  const s = SIZES[size];
  const drawer = variant === 'drawer';
  const ctrl = variant === 'ctrl';
  const boxed = drawer || ctrl;
  const boxH = drawer ? DRAWER_BTN_H : CTRL_BOX;
  const boxR = drawer ? DRAWER_BTN_R : CTRL_R;
  const disc = boxed ? boxH : (discSize ?? s.disc);
  /* The drawer look is one box: the sheet paints the button itself, the disc stays a transparent glyph holder. */
  const box = boxed ? boxH : size === 'dense' ? disc : TOUCH_MIN;
  const down = press && !disabled;
  const hasBadge = badge !== undefined && badge > 0;
  const name = `${label}${hasBadge ? countLabel(badge) : dot ? ', new activity' : ''}`;
  const ink = color ?? (variant === 'solid' ? T.onAccent : T.label);
  const discBg = boxed
    ? 'transparent'
    : variant === 'solid'
      ? T.accent
      : variant === 'filled' || down || pressed
        ? T.bgElev
        : 'transparent';
  const Comp = (href !== undefined ? 'a' : 'button') as ElementType;

  const release = (handler?: (e: PointerEvent<HTMLButtonElement>) => void) => (e: PointerEvent<HTMLButtonElement>) => {
    setPress(false);
    handler?.(e);
  };

  const semantics =
    Comp === 'button'
      ? { type: type ?? 'button', disabled: disabled || undefined }
      : { href, tabIndex: disabled ? -1 : undefined };

  /* A recon, marketing or shop-float look lives wholly in its sheet: no inline style but the caller's, no disc, no badge. */
  if (variant === 'recon' || variant === 'recon-overlay' || variant === 'recon-step' || variant === 'marketing-theme' || variant === 'marketing-dialog-close' || variant === 'marketing-menu' || variant === 'shop-float' || variant === 'marketing-scroll') {
    return (
      <Comp
        ref={ref}
        data-rv-icon-button=""
        data-rv-variant={variant}
        className={className}
        aria-label={label}
        aria-pressed={pressed}
        {...semantics}
        {...rest}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        style={variant === 'marketing-theme' ? { '--rv-theme-cycle-size': `${discSize ?? 36}px`, ...style } as CSSProperties : variant === 'marketing-scroll' ? { borderRadius: radius.pill, ...style } : style}
      >
        {renderIconSlot(icon, s.glyph)}
      </Comp>
    );
  }

  /* The Brief V2 look lives in its sheet; the box is the only inline style besides the caller's. */
  if (variant === 'wedge-bv2') {
    const box = Math.max(discSize ?? WEDGE_ICON_BUTTON_DEFAULT, WEDGE_ICON_BUTTON_DEFAULT);
    return (
      <Comp
        ref={ref}
        data-rv-icon-button=""
        data-rv-variant={variant}
        className={className}
        aria-label={label}
        aria-pressed={pressed}
        aria-disabled={disabled || undefined}
        {...semantics}
        {...rest}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
        style={{ width: box, height: box, minWidth: WEDGE_ICON_BUTTON_MIN, minHeight: WEDGE_ICON_BUTTON_MIN, ...style }}
      >
        {renderIconSlot(icon, WEDGE_ICON_BUTTON_GLYPH)}
      </Comp>
    );
  }

  return (
    <Comp
      ref={ref}
      data-rv-icon-button=""
      data-rv-variant={variant}
      data-rv-size={size}
      className={className}
      aria-label={name}
      aria-pressed={pressed}
      aria-disabled={disabled || undefined}
      {...semantics}
      {...rest}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      onPointerDown={(e: PointerEvent<HTMLButtonElement>) => {
        if (!disabled) setPress(true);
        onPointerDown?.(e);
      }}
      onPointerUp={release(onPointerUp)}
      onPointerLeave={release(onPointerLeave)}
      onPointerCancel={release(onPointerCancel)}
      style={{
        position: 'relative',
        boxSizing: 'border-box',
        width: box,
        height: box,
        minWidth: size === 'dense' || boxed ? undefined : TOUCH_MIN,
        padding: 0,
        margin: 0,
        ...(drawer ? null : ctrl ? { border: `1px solid ${T.separator}`, background: consoleRole('ctrl-fill', T.bgElev) } : { border: 0, background: 'transparent' }),
        borderRadius: boxed ? boxR : radius.pill,
        ...(ctrl ? { fontSize: 12, fontFamily: T.mono } : null),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...(drawer && color === undefined ? null : { color: ink }),
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(drawer ? null : { opacity: disabled ? DISABLED_OPACITY : 1 }),
        textDecoration: 'none',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        ...style,
      }}
    >
      <span
        data-rv-icon-button-disc=""
        style={{
          position: 'relative',
          boxSizing: 'border-box',
          width: boxed ? '100%' : disc,
          height: boxed ? '100%' : disc,
          borderRadius: boxed ? boxR : radius.pill,
        ...(ctrl ? { fontSize: 12, fontFamily: T.mono } : null),
          background: discBg,
          border: variant === 'filled' ? `${HAIRLINE}px solid ${T.separatorBold}` : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: down && !reduced && !boxed ? `scale(${PRESS_SCALE})` : 'scale(1)',
          transition: ['transform', 'background', 'box-shadow'].map((p) => `${p} ${duration.press}ms ${T.ease}`).join(', '),
        }}
      >
        {drawer ? renderIconSlot(icon, DRAWER_IB_ICON, undefined, DRAWER_GLYPH_WEIGHT) : renderIconSlot(icon, s.glyph)}
        {hasBadge ? <CountBadge count={badge} /> : null}
        {!hasBadge && dot ? (
          <span
            data-rv-icon-button-dot=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: DOT_INSET,
              right: DOT_INSET,
              width: DOT,
              height: DOT,
              boxSizing: 'border-box',
              borderRadius: radius.pill,
              background: T.accent,
              border: `${DOT_RIM}px solid ${T.bg}`,
            }}
          />
        ) : null}
      </span>
    </Comp>
  );
});
